import { Backpack } from 'lucide-preact'
import { useEffect, useState } from 'preact/hooks'
import { text } from '#contract'
import { useClient } from '../app/client-context.ts'
import { useView } from '../app/use-view.ts'
import { Slot } from '../inventory/slot.tsx'
import { useT } from '../i18n/index.ts'
import { isTouchPlaying } from '../touch/touch-controls.tsx'
import { ActionHint } from './action-hint.tsx'
import styles from './quick-access-bar.module.css'

/** How long the active item's name stays up after a change, in ms. */
const LABEL_MS = 1500

/**
 * The player's QuickAccess, bottom centre: nine slots, the active one outlined. The item's name
 * and what using it does show above for a moment after the slot or its item changes. On touch
 * a tap chooses a slot, holding the active one drops its stack, and a bag button beside the
 * bar opens the inventory.
 */
export function QuickAccessBar() {
  const size = useView((view) => view.inventory?.quickAccess.slots.length ?? 0)
  const touch = useView(isTouchPlaying)
  if (size === 0) return null

  return (
    <div class={styles.bar}>
      <ActiveLabel />
      <div class={styles.row}>
        <ol class={styles.slots} data-touch={touch || undefined}>
          {Array.from({ length: size }, (_, index) => (
            <BarSlot key={index} index={index} touch={touch} />
          ))}
        </ol>
        {touch && <BagButton />}
      </div>
    </div>
  )
}

/** Touch's inventory key: opens the player's own panel. */
function BagButton() {
  const t = useT()
  const client = useClient()
  return (
    <button
      type="button"
      class={styles.bag}
      tabIndex={-1}
      aria-label={t(text('inventory.open'))}
      onClick={() => {
        client?.inventory.openOwn()
      }}
    >
      <Backpack aria-hidden="true" />
    </button>
  )
}

function BarSlot({ index, touch }: { readonly index: number; readonly touch: boolean }) {
  const client = useClient()
  const slot = useView((view) => view.inventory?.quickAccess.slots[index] ?? null)
  const active = useView((view) => view.inventory?.active === index)

  // Hidden while dropping (a pause, another device): let go of it.
  useEffect(
    () => () => {
      if (touch) client?.touch.setButton('drop', false)
    },
    [client, touch],
  )

  const release = (): void => {
    client?.touch.setButton('drop', false)
  }
  return (
    <li
      class={styles.item}
      onPointerDown={
        touch
          ? (event) => {
              event.currentTarget.setPointerCapture(event.pointerId)
              if (active) client?.touch.setButton('drop', true)
              else client?.inventory.select(index)
            }
          : undefined
      }
      onPointerUp={touch ? release : undefined}
      onPointerCancel={touch ? release : undefined}
    >
      <Slot slot={slot} number={index + 1} active={active} />
    </li>
  )
}

function ActiveLabel() {
  const t = useT()
  const active = useView((view) => view.inventory?.active ?? null)
  const item = useView((view) => {
    const inventory = view.inventory
    const slot = inventory?.active ?? null
    return (slot === null ? null : inventory?.quickAccess.slots[slot]?.item) ?? null
  })
  const use = useView((view) => view.inventory?.use ?? null)
  const [open, setOpen] = useState(false)
  const id = item?.id ?? null

  useEffect(() => {
    setOpen(id !== null)
    if (id === null) return
    const timer = setTimeout(() => {
      setOpen(false)
    }, LABEL_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [active, id])

  return (
    <div class={styles.label} data-open={(open && item !== null) || undefined} role="status">
      {item && <p class={styles.name}>{t(item.name)}</p>}
      {item && use && (
        <p class={styles.use}>
          <ActionHint prompt={use} />
        </p>
      )}
    </div>
  )
}
